import React from 'react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactClick?: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose, onContactClick }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
            <p className="text-gray-400 text-sm mt-1">Effective Date: November 21, 2025</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 text-gray-300">
          <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg mb-6">
            <strong className="text-blue-300">Quick Summary:</strong>
            <span className="text-gray-300"> Chord Notator helps musicians create and manage chord charts. We only collect the minimum information necessary to provide this service and sync your charts across devices using Google Drive.</span>
          </div>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">1. Information We Collect</h3>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">Account Information</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Google Account Data:</strong> When you sign in with Google, we receive your name, email address, and profile picture</li>
              <li><strong className="text-gray-200">User ID:</strong> A unique identifier to link your account to your saved charts</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">Chart Data</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Musical Content:</strong> Chord progressions, song titles, lyrics, and musical arrangements you create</li>
              <li><strong className="text-gray-200">Collections:</strong> Chart collections and setlists you organize</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">Technical Information</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Usage Data:</strong> Basic analytics to improve app performance (no personal identification)</li>
              <li><strong className="text-gray-200">Device Information:</strong> Browser type and device type for compatibility</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">2. How We Use Your Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Account Management:</strong> To authenticate and identify you in the app</li>
              <li><strong className="text-gray-200">Data Synchronization:</strong> To save and sync your charts across your devices using Google Drive</li>
              <li><strong className="text-gray-200">Service Improvement:</strong> To analyze usage patterns and improve the app (anonymized data only)</li>
              <li><strong className="text-gray-200">Customer Support:</strong> To respond to your questions and provide technical support</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">3. Google Drive Integration</h3>
            <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg mb-4">
              <strong className="text-blue-300">Important:</strong>
              <span className="text-gray-300"> Chord Notator uses Google Drive to store your musical content securely in your own Google Drive account.</span>
            </div>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">File Storage:</strong> Your charts are saved as private files in a dedicated "Jazz Chord Notator" folder in your Google Drive</li>
              <li><strong className="text-gray-200">Access Scope:</strong> We only access files created by our app - we cannot see other files in your Google Drive</li>
              <li><strong className="text-gray-200">Data Ownership:</strong> All chart data stored in Google Drive belongs to you and remains under your control</li>
              <li><strong className="text-gray-200">Deletion:</strong> You can delete the "Jazz Chord Notator" folder from your Google Drive at any time</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">4. Information Sharing</h3>
            <p className="mb-3"><strong className="text-gray-200">We do not sell, rent, or share your personal information with third parties except:</strong></p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">With Your Consent:</strong> When you explicitly choose to share charts with other users</li>
              <li><strong className="text-gray-200">Service Providers:</strong> Google (for authentication and Drive storage), Vercel (for hosting), Firebase (for database)</li>
              <li><strong className="text-gray-200">Legal Requirements:</strong> If required by law or to protect our rights and safety</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">5. Data Security</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Encryption:</strong> All data is transmitted using HTTPS encryption</li>
              <li><strong className="text-gray-200">Authentication:</strong> Secure Google OAuth 2.0 authentication</li>
              <li><strong className="text-gray-200">Access Control:</strong> Your data is only accessible to you through your authenticated account</li>
              <li><strong className="text-gray-200">Storage:</strong> Chart data is stored in your personal Google Drive with Google's security measures</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">6. Your Rights and Choices</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Access:</strong> View all your saved charts in the app or directly in Google Drive</li>
              <li><strong className="text-gray-200">Deletion:</strong> Delete individual charts or your entire account</li>
              <li><strong className="text-gray-200">Export:</strong> Download your charts as JSON files</li>
              <li><strong className="text-gray-200">Revoke Access:</strong> Disconnect Chord Notator from your Google account at any time</li>
              <li><strong className="text-gray-200">Data Portability:</strong> Export your data in a machine-readable format</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">7. Children's Privacy</h3>
            <p>Chord Notator is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you are a parent and believe your child has provided us with personal information, please contact us.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">8. International Data Transfers</h3>
            <p>Your data may be processed and stored in servers located in different countries. By using Chord Notator, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">9. Changes to This Policy</h3>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the effective date.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">10. Third-Party Services</h3>
            <p className="mb-3">Chord Notator integrates with the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Google Services:</strong> Authentication, Drive storage, and People API</li>
              <li><strong className="text-gray-200">Stripe:</strong> Payment processing for premium features</li>
              <li><strong className="text-gray-200">Vercel:</strong> Web hosting and content delivery</li>
              <li><strong className="text-gray-200">Firebase:</strong> User authentication and database services</li>
            </ul>
          </section>

          <section className="mb-6 bg-gray-800/50 p-4 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">11. Contact Information</h3>
            <p className="mb-3">If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-gray-200">Contact:</strong>{' '}
                <button
                  onClick={() => {
                    onContactClick?.();
                    onClose();
                  }}
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  Click here to contact us
                </button>
              </li>
              <li><strong className="text-gray-200">Website:</strong> <a href="https://chordnotator.com" className="text-cyan-400 hover:text-cyan-300 underline">https://chordnotator.com</a></li>
            </ul>
            <p className="mt-3">We will respond to your inquiry within 30 days.</p>
          </section>

          <div className="text-center text-gray-500 text-sm py-4">
            Last updated: November 21, 2025
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
